import React, { useState, useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Container, Row, Col, Card, CardBody, Button } from 'reactstrap';
import QuestionBuilder from '../../../components/QuestionBank/QuestionBuilder';
import QuestionsList from '../../../components/QuestionBank/QuestionsList';
import { fetchQuestionById } from '../../../redux/QuestionBank/QuestionsAction';

// Error boundary to prevent crashes from propagating
class QuestionPageErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('CreateQuestionPage error boundary caught:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Card className="shadow mt-4">
          <CardBody className="text-center py-5">
            <i className="fas fa-exclamation-triangle fa-3x text-warning mb-3"></i>
            <h3>Something went wrong</h3>
            <p className="text-muted">
              An error occurred while loading this section. Please try refreshing the page.
            </p>
            <Button
              color="primary"
              onClick={() => this.setState({ hasError: false, error: null })}
            >
              <i className="fas fa-redo mr-2"></i>
              Try Again
            </Button>
          </CardBody>
        </Card>
      );
    }
    return this.props.children;
  }
}

const CreateQuestionPage = ({ userDetails }) => {
  const userEmail = userDetails?.email || '';
  const [editingQuestion, setEditingQuestion] = useState(null);
  const dispatch = useDispatch();
  const location = useLocation();

  // Support ?edit=questionId query param (e.g. from Feedback page)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const editQuestionId = params.get('edit');
    if (editQuestionId) {
      dispatch(fetchQuestionById(editQuestionId)).then(result => {
        if (result.success && result.question) {
          setEditingQuestion(result.question);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      });
    }
  }, [dispatch, location.search]);

  return (
    <>
      {/* Header */}
      <div className="header bg-gradient-success pb-8 pt-5 pt-md-8">
        <Container fluid>
          <div className="header-body">
            <h1 className="text-white mb-0">Create Question</h1>
            <p className="text-white mt-0 mb-0">Create and manage individual questions for the question bank</p>
          </div>
        </Container>
      </div>

      {/* Page content */}
      <Container className="mt--7" fluid>
        <QuestionPageErrorBoundary>
          <Row>
            <Col lg="12">
              <QuestionBuilder
                userEmail={userEmail}
                editingQuestion={editingQuestion}
                preselectedPassage={null}
                onQuestionSaved={() => {
                  setEditingQuestion(null);
                }}
              />
            </Col>
          </Row>
          <Row>
            <Col lg="12">
              <QuestionsList
                onEditQuestion={(question) => {
                  setEditingQuestion(question);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              />
            </Col>
          </Row>
        </QuestionPageErrorBoundary>
      </Container>
    </>
  );
};

const mapStateToProps = (state) => ({
  userDetails: state.loginAuth.userDetails
});

export default connect(mapStateToProps)(CreateQuestionPage);
